import BaseLayout from '@/layout/BaseLayout';
import { Button } from '@shelflix/ui-web/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@shelflix/ui-web/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  scrollToError,
} from '@shelflix/ui-web/components/ui/form';
import { Input } from '@shelflix/ui-web/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shelflix/ui-web/components/ui/select';
import { useForm } from 'react-hook-form';

import { useMaterialFormStore } from '@/screens/MaterialCreate/store';
import { useUserStore } from '@/store/userStore';
import Icon from '@shelflix/ui-web/components/Icon';
import { useEffect, useRef, useState } from 'react';

import { CreateFacilityModal } from '@/components/CreateFacilityModal';
import { GenericSelect } from '@/components/GenericSelect';
import { useHasScopes } from '@/hooks/useHasScopes';
import { EDeviceManagerType, ELedPanelOutputResolution, ELedPanelTarget } from '@shelflix/schemas/material/enums';
import { queryClient, trpc } from '@/trpcClient';
import { CampaignDto } from '@shelflix/trpc/routes/campaign/list/useCase';
import { FacilityDto } from '@shelflix/trpc/routes/facility/list/useCase';
import { useMutation, useQuery } from '@tanstack/react-query';

import { checkMaterialCreateInput, CreateMaterialInputType } from '@shelflix/schemas/material/create';
import { MaterialTypeDto } from '@shelflix/trpc/routes/materialType/list/useCase';
import { useNavigate } from 'react-router-dom';

import { CampaignCard } from '@/components/CampaignCardVertical';
import GridCampaignCard from '@/components/layout/GridCampaignCard';
import { usePreventLeave } from '@/utils/usePreventLeave';
import Dropzone from '@shelflix/ui-web/components/Dropzone/index';
import { toast } from 'sonner';
import { DisplayInfoSection } from './DisplayInfoSection';
import { TagsDropdown } from '@/components/TagsDropdown';
import { DatePicker } from '@shelflix/ui-web/components/DatePicker/index';

export interface ScheduleCampaign {
  campaign: CampaignDto;
  startDate: string;
  endDate: string;
}

export function MaterialCreate() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isCreateFacilityModalOpen, setIsCreateFacilityModalOpen] = useState(false);

  // Permission check for creating facilities
  const canCreateFacility = useHasScopes(['admin:facilities:create']);

  const { formData, resetForm } = useMaterialFormStore();
  const user = useUserStore((s) => s.user);
  const isMaster = user?.isMaster ?? false;

  const [availableCampaign, setAvailableCampaign] = useState<CampaignDto[]>([]);
  const createMaterialMutation = useMutation(trpc.material.create.mutationOptions());
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [contract, setContract] = useState<File | null>(null);

  const [defaultCampaign, setDefaultCampaign] = useState<CampaignDto | null>(null);

  const [selectedFacility, setSelectedFacility] = useState<FacilityDto | null>(null);
  const [selectedMaterialType, setSelectedMaterialType] = useState<MaterialTypeDto | null>(null);
  const [brandUuid, setBrandUuid] = useState<string | null>(null);
  const [deviceManagerType, setDeviceManagerType] = useState<EDeviceManagerType>(EDeviceManagerType.MAGICINFO);
  // FIREBASE and LED_PANEL are the same app-managed device - LED_PANEL just adds panel geometry fields.
  const isFirebaseFamily =
    deviceManagerType === EDeviceManagerType.FIREBASE || deviceManagerType === EDeviceManagerType.LED_PANEL;
  // Undefined behaves as TVBOX_HDMI (default target, no separate ESP32 fields)
  const [ledPanelTarget, setLedPanelTarget] = useState<ELedPanelTarget | undefined>(undefined);
  const isEsp32Target = ledPanelTarget === ELedPanelTarget.ESP32_HUB75;

  const [selectedCampaign, setSelectedCampaign] = useState<CampaignDto | null>(null);
  const [selectedTags, setSelectedTags] = useState<{ uuid: string; name: string }[]>([]);

  const [instalationDate, setInstalationDate] = useState<Date | undefined>(undefined);

  const navigate = useNavigate();

  const form = useForm<CreateMaterialInputType>({
    defaultValues: {
      ...formData,
    },
    mode: 'all',
  });

  const onSubmit = (data: CreateMaterialInputType) => {
    if (createMaterialMutation.isPending) return;
    setIsSubmitting(true);

    if (!defaultCampaign) {
      toast.error('A campanha padrão é obrigatória');
      return;
    }

    if (!selectedMaterialType) {
      toast.error('O tipo de material é obrigatório');
      return;
    }

    if (!selectedFacility) {
      toast.error('O estabelecimento é obrigatório');
      return;
    }

    const payload = {
      ...data,
      name: selectedMaterialType.name,
      contractType: contract?.type,
      materialType: 'image/png',
      brand: selectedMaterialType.brand.uuid,
      facility: selectedFacility.uuid,
      defaultCampaignUuid: defaultCampaign.uuid,
      currentCampaignUuid: defaultCampaign.uuid,
      exhibitionPoint: data.exhibitionPoint?.trim() === '' ? undefined : data.exhibitionPoint,
      deviceManagerType: deviceManagerType,
      firebaseVerificationCode: isFirebaseFamily ? data.firebaseVerificationCode : undefined,
      // Display is only relevant for MagicInfo - exclude for Firebase/LED Panel to avoid validation issues
      display: isFirebaseFamily ? undefined : data.display,
      ledControllerId: data.ledControllerId,
      ledPanelWidthPx: deviceManagerType === EDeviceManagerType.LED_PANEL ? data.ledPanelWidthPx : undefined,
      ledPanelHeightPx: deviceManagerType === EDeviceManagerType.LED_PANEL ? data.ledPanelHeightPx : undefined,
      ledPanelTarget: deviceManagerType === EDeviceManagerType.LED_PANEL ? ledPanelTarget : undefined,
      // Offset/output resolution only apply to the TVBOX_HDMI target (no HDMI canvas on ESP32)
      ledPanelOffsetXPx:
        deviceManagerType === EDeviceManagerType.LED_PANEL && !isEsp32Target ? data.ledPanelOffsetXPx : undefined,
      ledPanelOffsetYPx:
        deviceManagerType === EDeviceManagerType.LED_PANEL && !isEsp32Target ? data.ledPanelOffsetYPx : undefined,
      ledPanelOutputResolution:
        deviceManagerType === EDeviceManagerType.LED_PANEL && !isEsp32Target
          ? data.ledPanelOutputResolution
          : undefined,
      // Fps only applies to the ESP32_HUB75 target
      ledPanelFps: deviceManagerType === EDeviceManagerType.LED_PANEL && isEsp32Target ? data.ledPanelFps : undefined,
      tags: selectedTags.map((tag) => tag.uuid),
      installedAt: instalationDate ? instalationDate.toISOString() : undefined,
    };

    createMaterialMutation.mutate(payload, {
      onSuccess: async (response) => {
        try {
          if (contract && response.contractUrl) {
            setIsUploading(true);
            const formData = new FormData();
            Object.entries(response.contractUrl.fields ?? {}).forEach(([key, value]) => {
              formData.append(key, value);
            });
            formData.append('file', contract);

            await fetch(response.contractUrl.url, {
              method: 'POST',
              body: formData,
            });
          }
          resetForm();

          navigate('/pos', { state: { toastSuccess: true, code: response.code } });
        } catch (error) {
          toast.error(`Erro no upload: ${error.message}`);
        } finally {
          setIsUploading(false);
        }
      },
      onError: (error) => {
        toast.error(`Erro ao cadastrar material: ${error.message}`);
      },
      onSettled: () => {
        setIsSubmitting(false);
      },
    });
  };

  usePreventLeave(isUploading);

  const dropzoneRef = useRef<HTMLDivElement>(null);

  const queryMaterialType = useQuery(
    trpc.materialType.list.queryOptions({
      recordsPerPage: 9999,
    })
  );

  useEffect(() => {
    if (selectedMaterialType) {
      setBrandUuid(selectedMaterialType.brand.uuid);
      refetchCampaigns();
    }
  }, [selectedMaterialType]);

  const queryFacility = useQuery({
    ...trpc.facility.list.queryOptions({
      recordsPerPage: 9999,
    }),
  });

  const {
    data,
    isLoading,
    isError,
    refetch: refetchCampaigns,
  } = useQuery({
    ...trpc.campaign.listAvaliable.queryOptions({
      brandUuid: brandUuid ?? undefined,
      cityIbgeCode: selectedFacility?.city.ibgeCode ?? '',
      stateIbgeCode: selectedFacility?.state.ibgeCode ?? '',
    }),
    enabled: !!selectedMaterialType,
  });

  const campaigns = data?.campaigns ?? [];

  useEffect(() => {
    if (!isLoading && !isError && campaigns.length) {
      const defaultUuid = defaultCampaign?.uuid;

      const filteredCampaigns = campaigns.filter((item) => item.uuid !== defaultUuid);

      setAvailableCampaign(filteredCampaigns);
    }
  }, [campaigns, isLoading, isError, defaultCampaign]);

  const handlAddDefaultCampaign = (campaign: CampaignDto) => {
    setDefaultCampaign(campaign);
    setSelectedCampaign(null);
  };

  const { data: displays } = useQuery({
    ...trpc.material.listDisplays.queryOptions(),
    enabled: true,
  });

  return (
    <BaseLayout>
      <div className="px-2 md:px-0">
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit, () => {
              scrollToError();
            })}
            className="w-full"
          >
            <div className="flex w-full flex-col gap-4 sm:gap-8">
              <section className="w-full">
                <Card className="rounded-[10px]">
                  <CardHeader className="pb-0">
                    <CardTitle className="text-primary text-2xl leading-5 font-semibold">
                      Informações de Material
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="flex w-full flex-col gap-8 sm:flex-row">
                    <div>
                      <FormField
                        control={form.control}
                        name="materialType"
                        rules={{ validate: (_) => checkMaterialCreateInput('materialType', 'image/png') }}
                        render={({ field }) => (
                          <FormItem className="mt-3 flex cursor-pointer flex-col gap-[3px]">
                            <FormControl>
                              {!field.value ? (
                                <Dropzone
                                  removable={false}
                                  // onFilesSelected={(files) => {
                                  //   // setThumbnail(files[0]);
                                  // }}
                                  ref={dropzoneRef}
                                  title="Selecione o tipo de material para carregar a imagem"
                                  description=""
                                  variant="square"
                                  requiredAspectRatio="1:1"
                                  // value={field.value ? [field.value] : undefined}
                                  readonly
                                />
                              ) : (
                                <img
                                  src={field.value}
                                  alt="thumbnail"
                                  className="aspect-square max-h-[176px] min-w-[176px] rounded-md"
                                />
                              )}
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className="w-full">
                      <div className="grid w-full grid-cols-6 gap-4 sm:flex-row sm:items-start">
                        <FormField
                          control={form.control}
                          name="materialTypeUuid"
                          rules={{ validate: (value) => checkMaterialCreateInput('materialTypeUuid', value) }}
                          render={({ field }) => (
                            <FormItem className="col-span-3 flex w-full flex-col gap-[3px]">
                              <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">
                                Tipo de material
                              </FormLabel>
                              <FormControl>
                                <GenericSelect<MaterialTypeDto>
                                  onSelect={(materialType) => {
                                    setSelectedMaterialType(materialType);
                                    field.onChange(materialType.uuid ?? materialType);
                                    form.setValue('materialType', materialType?.photoUrl ?? '');
                                  }}
                                  value={field.value}
                                  disabled={queryMaterialType.isFetching}
                                  loading={queryMaterialType.isLoading}
                                  fetchItems={async (search) => {
                                    const items = (queryMaterialType.data?.items ?? []).sort((a, b) =>
                                      a.name.localeCompare(b.name)
                                    );

                                    return !search
                                      ? Promise.resolve(items)
                                      : Promise.resolve(
                                          items.filter((d) =>
                                            d.name
                                              .normalize('NFD')
                                              .replace(/[\u0300-\u036f]/g, '')
                                              .toLowerCase()
                                              .includes(
                                                search
                                                  .normalize('NFD')
                                                  .replace(/[\u0300-\u036f]/g, '')
                                                  .toLowerCase()
                                              )
                                          )
                                        );
                                  }}
                                  placeholder="Selecione o tipo de material"
                                  renderItem={(materialType) => (
                                    <div className="flex flex-row gap-2">
                                      <img
                                        src={materialType.photoUrl}
                                        alt={materialType.name}
                                        className="max-w-10 rounded-xs object-cover"
                                      />
                                      <div>
                                        <div className="text-sm font-medium">{materialType.name}</div>
                                        <div className="text-muted-foreground text-xs">{materialType.brand.name}</div>
                                      </div>
                                    </div>
                                  )}
                                  listOnEmptySearch
                                  getItemLabel={(brand) => brand.name}
                                  getItemKey={(materialType) => materialType.uuid}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <Button
                          className="col-span-1 mt-4.5 h-[40px]"
                          type="button"
                          style={{
                            backgroundColor: contract ? 'var(--primary)' : '#717171',
                          }}
                          onClick={() => fileInputRef.current?.click()}
                        >
                          <Icon name="edit_document" color="#fff" size={22} />
                          <span className="[font-family:'Figtree'] text-sm">
                            {contract ? 'Alterar contrato' : 'Anexar contrato'}
                          </span>
                        </Button>
                        <input
                          ref={fileInputRef}
                          type="file"
                          hidden
                          accept=".pdf,.doc,.docx"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              setContract(file);
                            }
                          }}
                        />
                        <div className="col-span-2 flex w-full flex-col gap-1">
                          <span className="text-xs font-medium text-[var(--gray-primary)]">
                            Data de instalação <span className="text-muted-foreground">(opcional)</span>
                          </span>
                          <DatePicker
                            mode="single"
                            value={instalationDate}
                            onChange={(date) => {
                              if (date instanceof Date) {
                                setInstalationDate(date);
                              }
                            }}
                          />
                        </div>
                      </div>

                      <div className="mt-4 flex w-full flex-row items-start gap-4">
                        <div className="flex w-full flex-col gap-1">
                          <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">Marca</FormLabel>
                          <Input
                            className="h-10"
                            placeholder="Marca"
                            disabled
                            value={selectedMaterialType?.brand?.name}
                          />
                        </div>

                        <div className="flex w-full flex-col gap-1">
                          <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">
                            Número de série
                          </FormLabel>
                          <Input
                            className="h-10"
                            placeholder="Número de série"
                            disabled
                            value={selectedMaterialType?.brand.code}
                          />
                        </div>
                        <div className="flex w-full flex-col gap-1">
                          <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">Tags</FormLabel>
                          <TagsDropdown selectedTags={selectedTags} setSelectedTags={setSelectedTags} />
                        </div>
                      </div>
                      <div className="mt-4 flex w-full flex-col gap-4 sm:flex-row">
                        <FormField
                          control={form.control}
                          name="cnpj"
                          rules={{ validate: (value) => checkMaterialCreateInput('cnpj', value) }}
                          render={({ field }) => (
                            <FormItem className="flex w-full flex-col gap-[3px]">
                              <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">CNPJ</FormLabel>
                              <FormControl>
                                <GenericSelect<FacilityDto>
                                  onSelect={(facility) => {
                                    field.onChange(facility.cnpj ?? facility);
                                    setSelectedFacility(facility);
                                    refetchCampaigns();
                                  }}
                                  disabled={queryFacility.isFetching}
                                  loading={queryFacility.isLoading}
                                  dependencies={[queryFacility.data]}
                                  value={field.value}
                                  fetchItems={async (search) => {
                                    const items = (queryFacility.data?.facilities ?? []).sort((a, b) =>
                                      a.name.localeCompare(b.name)
                                    );

                                    return !search
                                      ? Promise.resolve(items)
                                      : Promise.resolve(
                                          items.filter((d) =>
                                            d.cnpj
                                              .replace(/\D/g, '')
                                              .toLowerCase()
                                              .includes(search.replace(/\D/g, '').toLowerCase())
                                          )
                                        );
                                  }}
                                  renderItem={(facility) => (
                                    <div>
                                      <div className="text-sm font-medium">{facility.name}</div>
                                      <div className="text-muted-foreground text-xs">{facility.cnpj}</div>
                                    </div>
                                  )}
                                  listOnEmptySearch
                                  placeholder="Selecione o estabelecimento"
                                  getItemLabel={(facility) => facility.cnpj}
                                  getItemKey={(facility) => facility.uuid}
                                  headerAction={
                                    canCreateFacility
                                      ? {
                                          label: 'Criar novo estabelecimento',
                                          icon: <Icon name="add" size={18} />,
                                          onClick: () => setIsCreateFacilityModalOpen(true),
                                        }
                                      : undefined
                                  }
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <div className="flex w-full flex-col gap-1">
                          <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">
                            Estabelecimento
                          </FormLabel>
                          <Input
                            className="h-[36px]"
                            placeholder="Estabelecimento"
                            disabled
                            value={selectedFacility?.name}
                          />
                        </div>
                        <FormField
                          control={form.control}
                          name="exhibitionPoint"
                          rules={{
                            validate: (value) =>
                              checkMaterialCreateInput('exhibitionPoint', value?.trim() === '' ? undefined : value),
                          }}
                          render={({ field }) => (
                            <FormItem className="flex w-full flex-col gap-[3px]">
                              <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">
                                Ponto de referência
                              </FormLabel>
                              <FormControl>
                                <Input
                                  className="h-[36px]"
                                  placeholder="Ex: Corredor X"
                                  {...field}
                                  value={field.value ?? ''}
                                  onChange={(e) => {
                                    const inputValue = e.target.value;
                                    form.setValue('exhibitionPoint', inputValue, {
                                      shouldValidate: true,
                                      shouldDirty: true,
                                    });
                                  }}
                                  onBlur={field.onBlur}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      {/* Device Manager Section - Only visible for master users */}
                      {isMaster && (
                        <div className="mt-4 flex w-full flex-col gap-4 sm:flex-row sm:items-end">
                          <FormItem className="flex w-full flex-col gap-[3px]">
                            <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">
                              Gerenciador de dispositivos
                            </FormLabel>
                            <Select
                              value={deviceManagerType}
                              onValueChange={(value) => {
                                setDeviceManagerType(value as EDeviceManagerType);
                                form.setValue('deviceManagerType', value as EDeviceManagerType);
                                if (
                                  value !== EDeviceManagerType.FIREBASE &&
                                  value !== EDeviceManagerType.LED_PANEL
                                ) {
                                  form.setValue('firebaseVerificationCode', undefined);
                                }
                                if (value !== EDeviceManagerType.LED_PANEL) {
                                  form.setValue('ledPanelWidthPx', undefined);
                                  form.setValue('ledPanelHeightPx', undefined);
                                  form.setValue('ledPanelOffsetXPx', undefined);
                                  form.setValue('ledPanelOffsetYPx', undefined);
                                  form.setValue('ledPanelOutputResolution', undefined);
                                  form.setValue('ledPanelTarget', undefined);
                                  form.setValue('ledPanelFps', undefined);
                                  setLedPanelTarget(undefined);
                                }
                              }}
                            >
                              <SelectTrigger className="h-[36px] w-full">
                                <SelectValue placeholder="Selecione o gerenciador" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value={EDeviceManagerType.MAGICINFO}>Samsung</SelectItem>
                                <SelectItem value={EDeviceManagerType.FIREBASE}>Smart</SelectItem>
                                <SelectItem value={EDeviceManagerType.LED_PANEL}>Painel LED</SelectItem>
                              </SelectContent>
                            </Select>
                          </FormItem>

                          <FormField
                            control={form.control}
                            name="firebaseVerificationCode"
                            rules={{
                              validate: (value) => {
                                if (!isFirebaseFamily) return true;
                                if (!value || value.length !== 8) {
                                  return 'Código de verificação deve ter 8 caracteres';
                                }
                                if (!/^[A-Z0-9]{8}$/.test(value)) {
                                  return 'Código deve conter apenas letras e números';
                                }
                                return true;
                              },
                            }}
                            render={({ field }) => (
                              <FormItem className="flex w-full flex-col gap-[3px]">
                                <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">
                                  Código de verificação
                                </FormLabel>
                                <FormControl>
                                  <Input
                                    className="h-[36px]"
                                    placeholder="Ex: A1B2C3D4"
                                    disabled={!isFirebaseFamily}
                                    {...field}
                                    value={field.value ?? ''}
                                    onChange={(e) => {
                                      const upperValue = e.target.value.toUpperCase().slice(0, 8);
                                      field.onChange(upperValue);
                                    }}
                                    maxLength={8}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <Button
                            type="button"
                            variant="outline"
                            className="h-[36px] shrink-0"
                            disabled={!isFirebaseFamily}
                            onClick={() => {
                              const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
                              let code = '';
                              for (let i = 0; i < 8; i++) {
                                code += chars.charAt(Math.floor(Math.random() * chars.length));
                              }
                              form.setValue('firebaseVerificationCode', code, {
                                shouldValidate: true,
                                shouldDirty: true,
                              });
                            }}
                          >
                            <Icon name="autorenew" size={16} />
                            <span className="ml-1">Gerar código</span>
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </section>

              <section className="w-full">
                <Card className="rounded-[10px]">
                  <CardHeader className="pb-0">
                    <CardTitle className="text-primary text-2xl leading-5 font-semibold">Modem</CardTitle>
                  </CardHeader>
                  <CardContent className="grid grid-cols-6 gap-4">
                    <FormField
                      control={form.control}
                      name="modem.marca"
                      rules={{ validate: (value) => checkMaterialCreateInput('modem.marca', value) }}
                      render={({ field }) => (
                        <FormItem className="col-span-2 flex w-full flex-col gap-[3px]">
                          <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">Marca</FormLabel>
                          <FormControl>
                            <Input className="h-[36px] w-full" placeholder="Ex: Aquário" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="modem.modelo"
                      rules={{ validate: (value) => checkMaterialCreateInput('modem.modelo', value) }}
                      render={({ field }) => (
                        <FormItem className="col-span-2 flex w-full flex-col gap-[3px]">
                          <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">Modelo</FormLabel>
                          <FormControl>
                            <Input className="h-[36px] w-full" placeholder="Ex: Aquario 7000" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="modem.identifierNumber"
                      rules={{ validate: (value) => checkMaterialCreateInput('modem.identifierNumber', value) }}
                      render={({ field }) => (
                        <FormItem className="col-span-2 flex w-full flex-col gap-[3px]">
                          <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">
                            Número do Identificador
                          </FormLabel>
                          <FormControl>
                            <Input className="h-[36px] w-full" placeholder="Ex: AQU_MF7_0001" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="modem.msisdn"
                      rules={{ validate: (value) => checkMaterialCreateInput('modem.msisdn', value) }}
                      render={({ field }) => (
                        <FormItem className="col-span-2 flex w-full flex-col gap-[3px]">
                          <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">Linha</FormLabel>
                          <FormControl>
                            <Input className="h-[36px] w-full" placeholder="Ex: 5531987441245" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="modem.imei"
                      rules={{ validate: (value) => checkMaterialCreateInput('modem.imei', value) }}
                      render={({ field }) => (
                        <FormItem className="col-span-2 flex w-full flex-col gap-[3px]">
                          <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">IMEI</FormLabel>
                          <FormControl>
                            <Input className="h-[36px] w-full" placeholder="Ex: 2198012982908102810" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="modem.monthlyDataLimit"
                      rules={{ validate: (value) => checkMaterialCreateInput('modem.monthlyDataLimit', value) }}
                      render={({ field }) => (
                        <FormItem className="col-span-2 flex w-full flex-col gap-[3px]">
                          <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">
                            Limite de Dados Mensal (GB)
                          </FormLabel>
                          <FormControl>
                            <Input className="h-[36px] w-full" defaultValue={'1'} {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </CardContent>
                </Card>
              </section>

              {!isFirebaseFamily && (
                <section className="w-full">
                  <Card className="rounded-[10px]">
                    <CardHeader className="pb-0">
                      <CardTitle className="text-primary text-2xl leading-5 font-semibold">Tela</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <DisplayInfoSection displays={displays ?? []} form={form} />
                    </CardContent>
                  </Card>
                </section>
              )}

              {deviceManagerType === EDeviceManagerType.LED_PANEL && (
                <section className="w-full">
                  <Card className="rounded-[10px]">
                    <CardHeader className="pb-0">
                      <CardTitle className="text-primary text-2xl leading-5 font-semibold">Painel LED</CardTitle>
                    </CardHeader>
                    <CardContent className="grid grid-cols-4 gap-4">
                      <FormField
                        control={form.control}
                        name="ledPanelTarget"
                        rules={{ validate: (value) => checkMaterialCreateInput('ledPanelTarget', value) }}
                        render={({ field }) => (
                          <FormItem className="col-span-2 flex w-full flex-col gap-[3px]">
                            <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">Alvo</FormLabel>
                            <Select
                              value={field.value ?? ELedPanelTarget.TVBOX_HDMI}
                              onValueChange={(value) => {
                                field.onChange(value);
                                setLedPanelTarget(value as ELedPanelTarget);
                                if (value === ELedPanelTarget.ESP32_HUB75) {
                                  form.setValue('ledPanelOffsetXPx', undefined);
                                  form.setValue('ledPanelOffsetYPx', undefined);
                                  form.setValue('ledPanelOutputResolution', undefined);
                                } else {
                                  form.setValue('ledPanelFps', undefined);
                                }
                              }}
                            >
                              <FormControl>
                                <SelectTrigger className="h-[36px] w-full">
                                  <SelectValue placeholder="Selecione o alvo" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value={ELedPanelTarget.TVBOX_HDMI}>TV box (HDMI)</SelectItem>
                                <SelectItem value={ELedPanelTarget.ESP32_HUB75}>Esp32-HUB75-Matrix</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="ledPanelWidthPx"
                        rules={{ validate: (value) => checkMaterialCreateInput('ledPanelWidthPx', value) }}
                        render={({ field }) => (
                          <FormItem className="flex w-full flex-col gap-[3px]">
                            <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">
                              Largura (px)
                            </FormLabel>
                            <FormControl>
                              <Input
                                className="h-[36px]"
                                type="number"
                                placeholder="Ex: 512"
                                {...field}
                                value={field.value ?? ''}
                                onChange={(e) => field.onChange(e.target.value === '' ? undefined : Number(e.target.value))}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="ledPanelHeightPx"
                        rules={{ validate: (value) => checkMaterialCreateInput('ledPanelHeightPx', value) }}
                        render={({ field }) => (
                          <FormItem className="flex w-full flex-col gap-[3px]">
                            <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">
                              Altura (px)
                            </FormLabel>
                            <FormControl>
                              <Input
                                className="h-[36px]"
                                type="number"
                                placeholder="Ex: 128"
                                {...field}
                                value={field.value ?? ''}
                                onChange={(e) => field.onChange(e.target.value === '' ? undefined : Number(e.target.value))}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      {isEsp32Target ? (
                        <FormField
                          control={form.control}
                          name="ledPanelFps"
                          rules={{ validate: (value) => checkMaterialCreateInput('ledPanelFps', value) }}
                          render={({ field }) => (
                            <FormItem className="flex w-full flex-col gap-[3px]">
                              <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">
                                FPS
                              </FormLabel>
                              <FormControl>
                                <Input
                                  className="h-[36px]"
                                  type="number"
                                  placeholder="Ex: 30"
                                  {...field}
                                  value={field.value ?? ''}
                                  onChange={(e) =>
                                    field.onChange(e.target.value === '' ? undefined : Number(e.target.value))
                                  }
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      ) : (
                        <>
                          <FormField
                            control={form.control}
                            name="ledPanelOffsetXPx"
                            rules={{ validate: (value) => checkMaterialCreateInput('ledPanelOffsetXPx', value) }}
                            render={({ field }) => (
                              <FormItem className="flex w-full flex-col gap-[3px]">
                                <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">
                                  Deslocamento X (px)
                                </FormLabel>
                                <FormControl>
                                  <Input
                                    className="h-[36px]"
                                    type="number"
                                    placeholder="Ex: 0"
                                    {...field}
                                    value={field.value ?? ''}
                                    onChange={(e) =>
                                      field.onChange(e.target.value === '' ? undefined : Number(e.target.value))
                                    }
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name="ledPanelOffsetYPx"
                            rules={{ validate: (value) => checkMaterialCreateInput('ledPanelOffsetYPx', value) }}
                            render={({ field }) => (
                              <FormItem className="flex w-full flex-col gap-[3px]">
                                <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">
                                  Deslocamento Y (px)
                                </FormLabel>
                                <FormControl>
                                  <Input
                                    className="h-[36px]"
                                    type="number"
                                    placeholder="Ex: 0"
                                    {...field}
                                    value={field.value ?? ''}
                                    onChange={(e) =>
                                      field.onChange(e.target.value === '' ? undefined : Number(e.target.value))
                                    }
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name="ledPanelOutputResolution"
                            rules={{ validate: (value) => checkMaterialCreateInput('ledPanelOutputResolution', value) }}
                            render={({ field }) => (
                              <FormItem className="flex w-full flex-col gap-[3px]">
                                <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">
                                  Resolução de saída da tvbox
                                </FormLabel>
                                <Select value={field.value ?? ''} onValueChange={field.onChange}>
                                  <FormControl>
                                    <SelectTrigger className="h-[36px] w-full">
                                      <SelectValue placeholder="Selecione a resolução" />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent>
                                    <SelectItem value={ELedPanelOutputResolution.HD_1280x720}>
                                      HD (1280x720)
                                    </SelectItem>
                                    <SelectItem value={ELedPanelOutputResolution.FULL_HD_1920x1080}>
                                      Full HD (1920x1080)
                                    </SelectItem>
                                  </SelectContent>
                                </Select>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </>
                      )}
                    </CardContent>
                  </Card>
                </section>
              )}

              <section className="w-full">
                <Card className="rounded-[10px]">
                  <CardHeader className="pb-0">
                    <CardTitle className="text-primary text-2xl leading-5 font-semibold">Controlador de Led</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <FormField
                      control={form.control}
                      name="ledControllerId"
                      rules={{ validate: (value) => checkMaterialCreateInput('ledController', value) }}
                      render={({ field }) => (
                        <FormItem className="flex w-full flex-col gap-[3px]">
                          <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">
                            Id do controlador
                          </FormLabel>
                          <FormControl>
                            <Input
                              className="h-[36px] w-[30%]"
                              placeholder="Ex: A1B2C3D4"
                              {...field}
                              value={field.value ?? ''}
                              onChange={(e) => {
                                const upperValue = e.target.value.toUpperCase().slice(0, 12);
                                field.onChange(upperValue);
                              }}
                              maxLength={12}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </CardContent>
                </Card>
              </section>

              {/* <section className="w-full">
                <Card className="rounded-[10px]">
                  <CardHeader className="pb-0">
                    <CardTitle className="text-primary text-2xl leading-5 font-semibold">Shelhub</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ShelhubInfoSection devices={devices ?? []} form={form} />
                  </CardContent>
                </Card>
              </section> */}

              <section className="w-full">
                <Card className="rounded-[10px]">
                  <CardHeader className="pb-0">
                    <CardTitle className="text-primary text-2xl leading-5 font-semibold">
                      Defina uma campanha padrão
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <GridCampaignCard>
                      {availableCampaign.map((campaign) => (
                        <CampaignCard
                          campaign={campaign}
                          key={campaign.uuid}
                          showSetAsDefault={campaign.uuid === selectedCampaign?.uuid}
                          showTitle={false}
                          actions={{
                            onClick: () => setSelectedCampaign(campaign),
                            onSetAsDefault: () => handlAddDefaultCampaign(campaign),
                            onCloseModal: () => setSelectedCampaign(null),
                          }}
                        />
                      ))}
                    </GridCampaignCard>
                  </CardContent>
                </Card>
              </section>
              <Card>
                <CardHeader className="pb-0">
                  <CardTitle className="text-2xl leading-5 font-semibold text-gray-500">Campanha padrão</CardTitle>
                </CardHeader>
                <CardContent>
                  <GridCampaignCard>
                    {defaultCampaign && (
                      <div className="flex items-center justify-center rounded-md border-2 border-gray-400 bg-gray-700/30 p-1">
                        <CampaignCard
                          campaign={defaultCampaign}
                          key={defaultCampaign.uuid}
                          actions={
                            {
                              // onPlayNow: () => onPlayCampaignNow?.(defaultCampaign.uuid),
                              // isPlaying: currentCampaign?.uuid == defaultCampaign.uuid,
                              // isDefault: true,
                            }
                          }
                        />
                      </div>
                    )}
                  </GridCampaignCard>
                </CardContent>
              </Card>

              <div className="mb-2 flex grow-0 justify-center">
                <Button
                  loading={createMaterialMutation.isPending || isUploading}
                  type="submit"
                  className="bg-primary hover:bg-primary flex grow cursor-pointer items-center gap-2.5 rounded-[5px] px-5 py-2.5 font-semibold text-white sm:grow-0"
                  disabled={isSubmitting}
                >
                  <Icon name="label" fill={1} color="#fff" size={16} />
                  <span className="[font-family:'Figtree'] text-sm">Finalizar Cadastro</span>
                </Button>
              </div>
            </div>
          </form>
        </Form>
      </div>

      {/* Create Facility Modal */}
      <CreateFacilityModal
        open={isCreateFacilityModalOpen}
        onOpenChange={setIsCreateFacilityModalOpen}
        onFacilityCreated={async (newFacility) => {
          // Refetch facilities to include the new one
          await queryClient.invalidateQueries({ queryKey: trpc.facility.list.queryKey() });
          await queryFacility.refetch();

          // Auto-select the newly created facility
          form.setValue('cnpj', newFacility.cnpj, { shouldValidate: true });
          setSelectedFacility({
            uuid: newFacility.uuid,
            name: newFacility.name,
            cnpj: newFacility.cnpj,
            city: {
              uuid: '',
              name: newFacility.city,
              ibgeCode: newFacility.cityIbgeCode,
            },
            state: {
              uuid: '',
              name: newFacility.state,
              ibgeCode: '', // State ibgeCode not needed for campaign filtering
            },
          } as FacilityDto);
          refetchCampaigns();
        }}
      />
    </BaseLayout>
  );
}
